import { useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type { Action as ProcessAction } from '../../../../bridge/types/action.ts';
import type { Artifact } from '../../../../bridge/types/artifact.ts';

export type ProcessChildrenState = 'idle' | 'loading' | 'ready' | 'error';

export interface UseProcessChildrenResult {
	artifacts: Artifact[];
	actions: ProcessAction[];
	state: ProcessChildrenState;
}

type ChildrenResponse = {
	artifacts?: Artifact[];
	actions?: ProcessAction[];
};

const EMPTY: UseProcessChildrenResult = {
	artifacts: [],
	actions: [],
	state: 'idle',
};

/**
 * The real artifacts and actions behind a built campaign.
 *
 * Fetched over REST rather than through the bridge's `do_load_setup_snapshot`,
 * because this is a read: the strategy panel already gets everything it shows
 * this way, and a passive read has no business writing N snapshots into shared
 * user state.
 *
 * `state` is four-valued on purpose. `idle` (no process yet) is NOT `ready` with
 * empty arrays — the first means the campaign has not been built, the second
 * means it was built and is genuinely empty, and a caller that conflates them
 * shows "nothing here" for work that is still 30 seconds away.
 */
export function useProcessChildren(
	processId: string | null | undefined,
): UseProcessChildrenResult {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const shouldFetch = !!processId && !!userId;

	const [result, setResult] = useState<UseProcessChildrenResult>(EMPTY);

	useEffect(() => {
		if (!shouldFetch) {
			setResult(EMPTY);
			return undefined;
		}
		let cancelled = false;
		setResult((prev) => ({ ...prev, state: 'loading' }));
		void bridgewayApi
			.get<ChildrenResponse>(
				`/api/user/${userId}/processes/${processId}/children`,
			)
			.then((res) => {
				if (cancelled) return;
				setResult({
					artifacts: res?.artifacts ?? [],
					actions: res?.actions ?? [],
					state: 'ready',
				});
			})
			.catch((err) => {
				if (cancelled) return;
				logger.error('Failed to load campaign children', err);
				// Empty AND 'error' — never a silent empty success, which would read as
				// "this campaign builds nothing" when the request simply failed.
				setResult({ artifacts: [], actions: [], state: 'error' });
			});
		return () => {
			cancelled = true;
		};
		// DEPENDENCIES ARE LOAD-BEARING. Key on the process id STRING only.
		//
		// A strategy bumps `strategy_dock_last_updated` up to 2N times while its
		// campaigns build (one when each claims `materializing`, one when each stamps
		// `materialized`), and every bump hands this component a brand-new `run`
		// object. Adding `run.updated_at`, the campaign proposal, or its `status` here
		// would turn each of those pushes into a refetch of every campaign — ~2N x N
		// requests for one panel open. The id is all that has to change: `process_id`
		// only exists once a campaign is built, so `undefined -> 'p-1'` is exactly the
		// transition worth refetching on, and it happens once.
	}, [shouldFetch, userId, processId, bridgewayApi]);

	return result;
}
